import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import apiClient from '@/utils/api';
import CatalogSelect from './CatalogSelect';

const PAY_TO_OPTIONS = [
  { value: 'indian_company', label: 'Indian Company Account' },
  { value: 'university',     label: 'University Account' },
  { value: 'hostel',         label: 'Hostel Account' },
  { value: 'other',          label: 'Other' },
];

// Backward-compat mapping for legacy values like "company" / "institution"
const PAY_TO_ALIAS = { company: 'indian_company', institution: 'other' };

const CURRENCIES = [
  { value: 'INR', symbol: '₹', label: 'INR (₹)' },
  { value: 'USD', symbol: '$', label: 'USD ($)' },
  { value: 'EUR', symbol: '€', label: 'EUR (€)' },
  { value: 'GBP', symbol: '£', label: 'GBP (£)' },
  { value: 'AUD', symbol: 'A$', label: 'AUD (A$)' },
  { value: 'CAD', symbol: 'C$', label: 'CAD (C$)' },
];

const symbolOf = (c) => (CURRENCIES.find(x => x.value === c) || CURRENCIES[0]).symbol;

const emptyItem = () => ({
  id: `new_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
  title: '',
  description: '',
  amount: 0,
  currency: 'INR',
  due_label: '',
  payee_type: 'indian_company',
  payee_name: '',
});

const emptyForm = {
  name: '',
  countries: [],
  programs: [],
  universities: [],
  is_active: true,
  items: [emptyItem()],
};

export default function PaymentSchedulesManager({ user }) {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [countries, setCountries] = useState([]);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [countryQuery, setCountryQuery] = useState('');
  const [programQuery, setProgramQuery] = useState('');
  const [universityQuery, setUniversityQuery] = useState('');
  const [programs, setPrograms] = useState([]);
  const [universities, setUniversities] = useState([]);
  const [fxRates, setFxRates] = useState({ INR: 1 });
  const [fxFetchedAt, setFxFetchedAt] = useState(null);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [tplRes, constRes, fxRes] = await Promise.all([
        apiClient.get('/student-auth/payment-schedule-templates'),
        apiClient.get('/constants'),
        apiClient.get('/student-auth/fx-rates').catch(() => ({ data: { rates: { INR: 1 } } })),
      ]);
      setTemplates(tplRes.data.templates || []);
      setCountries(constRes.data.countries || []);
      setPrograms(constRes.data.programs || []);
      setUniversities(constRes.data.universities || []);
      setFxRates(fxRes.data?.rates || { INR: 1 });
      setFxFetchedAt(fxRes.data?.last_fetched_at || null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load payment schedules');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadAll(); }, []);

  const openCreate = () => {
    setEditing(null);
    setForm({ ...emptyForm, items: [emptyItem()] });
    setCountryQuery(''); setProgramQuery(''); setUniversityQuery('');
    setEditorOpen(true);
  };

  const openEdit = (tpl) => {
    setEditing(tpl);
    const tplCountries = Array.isArray(tpl.countries) ? tpl.countries : (tpl.country ? [tpl.country] : []);
    const tplPrograms = Array.isArray(tpl.programs) ? tpl.programs : (tpl.program ? [tpl.program] : []);
    const tplUniversities = Array.isArray(tpl.universities) ? tpl.universities : [];
    const normItems = ((tpl.items && tpl.items.length) ? tpl.items : [emptyItem()]).map(it => ({
      ...it,
      currency: it.currency || 'INR',
      payee_type: PAY_TO_ALIAS[it.payee_type] || it.payee_type || 'indian_company',
    }));
    setForm({
      name: tpl.name || '',
      countries: tplCountries,
      programs: tplPrograms,
      universities: tplUniversities,
      is_active: tpl.is_active !== false,
      items: normItems,
    });
    setCountryQuery(''); setProgramQuery(''); setUniversityQuery('');
    setEditorOpen(true);
  };

  const toggleCountry = (c) => setForm(prev => ({
    ...prev,
    countries: prev.countries.includes(c) ? prev.countries.filter(x => x !== c) : [...prev.countries, c],
  }));

  const toggleProgram = (p) => setForm(prev => ({
    ...prev,
    programs: prev.programs.includes(p) ? prev.programs.filter(x => x !== p) : [...prev.programs, p],
  }));

  const toggleUniversity = (u) => setForm(prev => ({
    ...prev,
    universities: prev.universities.includes(u) ? prev.universities.filter(x => x !== u) : [...prev.universities, u],
  }));

  const updateItem = (idx, patch) => {
    setForm(prev => {
      const items = [...prev.items];
      items[idx] = { ...items[idx], ...patch };
      return { ...prev, items };
    });
  };

  const addItem = () => setForm(prev => ({ ...prev, items: [...prev.items, emptyItem()] }));

  const removeItem = (idx) => {
    setForm(prev => ({ ...prev, items: prev.items.filter((_, i) => i !== idx) }));
  };

  const moveItem = (idx, dir) => {
    setForm(prev => {
      const items = [...prev.items];
      const swap = idx + dir;
      if (swap < 0 || swap >= items.length) return prev;
      [items[idx], items[swap]] = [items[swap], items[idx]];
      return { ...prev, items };
    });
  };

  const totalAmountINR = form.items.reduce((sum, i) => {
    const rate = fxRates[(i.currency || 'INR').toUpperCase()] || 1;
    return sum + (Number(i.amount) || 0) * Number(rate);
  }, 0);

  const handleSave = async () => {
    if (!form.countries.length || !form.programs.length) {
      toast.error('Select at least one country and one program');
      return;
    }
    const validItems = form.items.filter(i => i.title.trim() && Number(i.amount) > 0);
    if (validItems.length === 0) {
      toast.error('Add at least one payment item with a title and amount');
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form, items: validItems };
      if (editing) {
        await apiClient.put(`/student-auth/payment-schedule-templates/${editing.id}`, payload);
        toast.success('Schedule updated');
      } else {
        await apiClient.post('/student-auth/payment-schedule-templates', payload);
        toast.success('Schedule created');
      }
      setEditorOpen(false);
      loadAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (tpl) => {
    if (!window.confirm(`Delete payment schedule "${tpl.name}"?`)) return;
    try {
      await apiClient.delete(`/student-auth/payment-schedule-templates/${tpl.id}`);
      toast.success('Schedule deleted');
      loadAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
    }
  };

  return (
    <div className="space-y-4" data-testid="payment-schedules-manager">
      <Card>
        <CardHeader className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
          <div>
            <CardTitle className="flex items-center gap-2">
              <i className="fas fa-rupee-sign text-emerald-600"></i>
              Payment Schedules
            </CardTitle>
            <CardDescription>
              Define the complete payment roadmap per <strong>country + program</strong>. Students see this in their Payments tab — they can pay only against actual invoices.
            </CardDescription>
          </div>
          <Button onClick={openCreate} className="bg-[#192650] hover:bg-[#0f1a38]" data-testid="create-schedule-btn">
            <i className="fas fa-plus mr-2"></i>New Schedule
          </Button>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="py-12 text-center text-gray-400">
              <i className="fas fa-spinner fa-spin mr-2"></i>Loading...
            </div>
          ) : templates.length === 0 ? (
            <div className="py-16 text-center" data-testid="no-schedules-empty">
              <i className="fas fa-map-signs text-4xl text-gray-300 mb-3"></i>
              <p className="text-sm font-semibold text-gray-700">No payment schedules defined yet</p>
              <p className="text-xs text-gray-500 mt-1">Publish a payment roadmap per country + program so students can plan ahead.</p>
              <Button onClick={openCreate} variant="outline" className="mt-4" size="sm">
                <i className="fas fa-plus mr-2"></i>Create Your First Schedule
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {templates.map(tpl => {
                const tplCountries = Array.isArray(tpl.countries) && tpl.countries.length ? tpl.countries : (tpl.country ? [tpl.country] : []);
                const tplPrograms = Array.isArray(tpl.programs) && tpl.programs.length ? tpl.programs : (tpl.program ? [tpl.program] : []);
                const tplUniversities = Array.isArray(tpl.universities) ? tpl.universities : [];
                return (
                <div
                  key={tpl.id}
                  className="rounded-lg border bg-white p-4 hover:border-emerald-300 transition-colors"
                  data-testid={`schedule-row-${tpl.id}`}
                >
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div className="min-w-0">
                      <h4 className="text-sm font-bold text-gray-900 truncate">{tpl.name}</h4>
                      <div className="flex flex-wrap items-center gap-1.5 mt-1">
                        {tplCountries.map(c => (
                          <Badge key={`c-${c}`} variant="outline" className="text-[10px] bg-indigo-50 text-indigo-700 border-indigo-200">
                            <i className="fas fa-flag mr-1"></i>{c}
                          </Badge>
                        ))}
                        {tplPrograms.map(p => (
                          <Badge key={`p-${p}`} variant="outline" className="text-[10px] bg-purple-50 text-purple-700 border-purple-200">
                            <i className="fas fa-graduation-cap mr-1"></i>{p}
                          </Badge>
                        ))}
                        {tplUniversities.length > 0 ? (
                          tplUniversities.map(u => (
                            <Badge key={`u-${u}`} variant="outline" className="text-[10px] bg-amber-50 text-amber-700 border-amber-200">
                              <i className="fas fa-university mr-1"></i>{u}
                            </Badge>
                          ))
                        ) : (
                          <Badge variant="outline" className="text-[10px] bg-slate-50 text-slate-600 border-slate-200">
                            <i className="fas fa-university mr-1"></i>All universities
                          </Badge>
                        )}
                        {tpl.is_active ? (
                          <Badge variant="outline" className="text-[10px] bg-emerald-50 text-emerald-700 border-emerald-200">
                            <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full mr-1.5"></span>Active
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="text-[10px] bg-gray-100 text-gray-600 border-gray-200">Inactive</Badge>
                        )}
                      </div>
                    </div>
                    <div className="flex shrink-0 gap-1">
                      <Button size="sm" variant="outline" onClick={() => openEdit(tpl)} data-testid={`edit-schedule-${tpl.id}`}>
                        <i className="fas fa-edit"></i>
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => handleDelete(tpl)} className="text-red-600 hover:bg-red-50" data-testid={`delete-schedule-${tpl.id}`}>
                        <i className="fas fa-trash"></i>
                      </Button>
                    </div>
                  </div>
                  <div className="mt-2 rounded bg-slate-50 p-2 text-[11px] text-gray-700">
                    <div className="flex justify-between">
                      <span className="text-gray-500">{tpl.items?.length || 0} payment stage{(tpl.items?.length || 0) !== 1 ? 's' : ''}</span>
                      <span className="font-bold text-emerald-700">Total ≈ ₹{Number(tpl.total_amount_inr || 0).toLocaleString('en-IN')}</span>
                    </div>
                  </div>
                </div>
              );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={editorOpen} onOpenChange={setEditorOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit Payment Schedule' : 'Create Payment Schedule'}</DialogTitle>
            <DialogDescription>
              Define all payments a student must make. Select multiple countries and programs — one schedule can cover many combinations (e.g. MBBS + MD across Georgia, Russia & Kyrgyzstan).
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1">
              <Label>Schedule Name</Label>
              <Input
                placeholder="e.g. MBBS — Georgia & Russia"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                data-testid="schedule-name-input"
              />
            </div>

            {/* Countries multi-select */}
            <div className="space-y-1">
              <Label>Countries * <span className="text-[10px] text-gray-500">(select one or more — students from any of these countries will see this schedule)</span></Label>
              <div className="rounded-lg border bg-slate-50 p-3 space-y-2">
                <div className="flex items-center gap-2 flex-wrap min-h-[22px]">
                  {form.countries.length === 0 ? (
                    <span className="text-[11px] text-gray-400 italic">No countries selected yet</span>
                  ) : (
                    form.countries.map(c => (
                      <Badge
                        key={c}
                        variant="outline"
                        className="bg-indigo-50 text-indigo-700 border-indigo-200 text-xs cursor-pointer hover:bg-indigo-100"
                        onClick={() => toggleCountry(c)}
                        data-testid={`schedule-country-chip-${c}`}
                      >
                        {c} <i className="fas fa-times ml-1.5 text-[9px]"></i>
                      </Badge>
                    ))
                  )}
                </div>
                <Input
                  placeholder="Search countries to add..."
                  value={countryQuery}
                  onChange={(e) => setCountryQuery(e.target.value)}
                  className="h-9 text-sm bg-white"
                  data-testid="schedule-country-search"
                />
                {countryQuery && (
                  <div className="max-h-40 overflow-y-auto bg-white rounded border divide-y">
                    {countries
                      .filter(c => c.toLowerCase().includes(countryQuery.toLowerCase()) && !form.countries.includes(c))
                      .slice(0, 20)
                      .map(c => (
                        <button
                          key={c}
                          type="button"
                          onClick={() => { toggleCountry(c); setCountryQuery(''); }}
                          className="w-full text-left px-3 py-1.5 text-xs hover:bg-slate-50 flex items-center justify-between"
                          data-testid={`add-schedule-country-${c}`}
                        >
                          <span>{c}</span>
                          <i className="fas fa-plus text-[10px] text-indigo-500"></i>
                        </button>
                      ))
                    }
                  </div>
                )}
              </div>
            </div>

            {/* Programs multi-select */}
            <div className="space-y-1">
              <Label>Programs * <span className="text-[10px] text-gray-500">(students applying to any of these programs will see this schedule)</span></Label>
              <div className="rounded-lg border bg-slate-50 p-3 space-y-2">
                <div className="flex items-center gap-2 flex-wrap min-h-[22px]">
                  {form.programs.length === 0 ? (
                    <span className="text-[11px] text-gray-400 italic">No programs selected yet</span>
                  ) : (
                    form.programs.map(p => (
                      <Badge
                        key={p}
                        variant="outline"
                        className="bg-purple-50 text-purple-700 border-purple-200 text-xs cursor-pointer hover:bg-purple-100"
                        onClick={() => toggleProgram(p)}
                        data-testid={`schedule-program-chip-${p}`}
                      >
                        {p} <i className="fas fa-times ml-1.5 text-[9px]"></i>
                      </Badge>
                    ))
                  )}
                </div>
                <Input
                  placeholder="Search programs to add..."
                  value={programQuery}
                  onChange={(e) => setProgramQuery(e.target.value)}
                  className="h-9 text-sm bg-white"
                  data-testid="schedule-program-search"
                />
                {programQuery && (
                  <div className="max-h-40 overflow-y-auto bg-white rounded border divide-y">
                    {programs
                      .filter(p => p.toLowerCase().includes(programQuery.toLowerCase()) && !form.programs.includes(p))
                      .slice(0, 20)
                      .map(p => (
                        <button
                          key={p}
                          type="button"
                          onClick={() => { toggleProgram(p); setProgramQuery(''); }}
                          className="w-full text-left px-3 py-1.5 text-xs hover:bg-slate-50 flex items-center justify-between"
                          data-testid={`add-schedule-program-${p}`}
                        >
                          <span>{p}</span>
                          <i className="fas fa-plus text-[10px] text-purple-500"></i>
                        </button>
                      ))
                    }
                  </div>
                )}
              </div>
            </div>

            {/* Universities multi-select (optional — empty = applies to all universities) */}
            <div className="space-y-1">
              <Label>Universities <span className="text-[10px] text-gray-500">(optional — leave empty to apply to all universities. Add specific universities when fees differ per institution)</span></Label>
              <div className="rounded-lg border bg-slate-50 p-3 space-y-2">
                <div className="flex items-center gap-2 flex-wrap min-h-[22px]">
                  {form.universities.length === 0 ? (
                    <span className="text-[11px] text-gray-400 italic">All universities (default)</span>
                  ) : (
                    form.universities.map(u => (
                      <Badge
                        key={u}
                        variant="outline"
                        className="bg-amber-50 text-amber-700 border-amber-200 text-xs cursor-pointer hover:bg-amber-100"
                        onClick={() => toggleUniversity(u)}
                        data-testid={`schedule-university-chip-${u}`}
                      >
                        {u} <i className="fas fa-times ml-1.5 text-[9px]"></i>
                      </Badge>
                    ))
                  )}
                </div>
                <Input
                  placeholder="Search universities to add..."
                  value={universityQuery}
                  onChange={(e) => setUniversityQuery(e.target.value)}
                  className="h-9 text-sm bg-white"
                  data-testid="schedule-university-search"
                />
                {universityQuery && (
                  <div className="max-h-40 overflow-y-auto bg-white rounded border divide-y">
                    {universities
                      .filter(u => u.toLowerCase().includes(universityQuery.toLowerCase()) && !form.universities.includes(u))
                      .slice(0, 20)
                      .map(u => (
                        <button
                          key={u}
                          type="button"
                          onClick={() => { toggleUniversity(u); setUniversityQuery(''); }}
                          className="w-full text-left px-3 py-1.5 text-xs hover:bg-slate-50 flex items-center justify-between"
                          data-testid={`add-schedule-university-${u}`}
                        >
                          <span>{u}</span>
                          <i className="fas fa-plus text-[10px] text-amber-500"></i>
                        </button>
                      ))
                    }
                    {universities.filter(u => u.toLowerCase().includes(universityQuery.toLowerCase()) && !form.universities.includes(u)).length === 0 && (
                      <div className="px-3 py-2 text-[11px] text-gray-400 italic">No matching universities. Add new ones via the Universities catalog in Settings.</div>
                    )}
                  </div>
                )}
              </div>
            </div>

            <div className="flex items-center justify-between gap-3 rounded-lg border bg-slate-50 px-3 py-2">
              <div className="flex items-center gap-3">
                <Label className="text-sm">Active</Label>
                <Switch checked={form.is_active} onCheckedChange={(v) => setForm({ ...form, is_active: v })} data-testid="schedule-active-toggle" />
                <span className="text-xs text-gray-600">
                  {form.is_active ? 'Visible to matching students' : 'Hidden'}
                </span>
              </div>
              <div className="text-[11px] text-gray-500 flex items-center gap-1.5" data-testid="fx-info">
                <i className="fas fa-sync-alt text-emerald-500"></i>
                FX live · 1 USD ≈ ₹{(fxRates.USD || 0).toFixed(2)}
                {fxFetchedAt && <span className="text-gray-400">· {new Date(fxFetchedAt).toLocaleString()}</span>}
              </div>
            </div>

            <div className="pt-3 border-t">
              <div className="flex items-center justify-between mb-2">
                <Label className="text-sm font-semibold">Payment Items</Label>
                <div className="text-xs font-semibold text-emerald-700">Total ≈ ₹{totalAmountINR.toLocaleString('en-IN', { maximumFractionDigits: 0 })}</div>
              </div>
              <div className="space-y-3">
                {form.items.map((item, idx) => {
                  const rate = fxRates[(item.currency || 'INR').toUpperCase()] || 1;
                  const inrEq = (Number(item.amount) || 0) * Number(rate);
                  return (
                  <div key={item.id} className="rounded-lg border bg-white p-3 space-y-2" data-testid={`schedule-item-${idx}`}>
                    <div className="flex items-center justify-between gap-2">
                      <div className="text-xs font-bold text-gray-700">#{idx + 1}</div>
                      <div className="flex gap-1">
                        <button type="button" onClick={() => moveItem(idx, -1)} disabled={idx === 0} className="text-xs text-gray-500 hover:text-gray-800 disabled:opacity-30 px-1"><i className="fas fa-arrow-up"></i></button>
                        <button type="button" onClick={() => moveItem(idx, +1)} disabled={idx === form.items.length - 1} className="text-xs text-gray-500 hover:text-gray-800 disabled:opacity-30 px-1"><i className="fas fa-arrow-down"></i></button>
                        {form.items.length > 1 && (
                          <button type="button" onClick={() => removeItem(idx)} className="text-xs text-red-500 hover:text-red-700 px-1"><i className="fas fa-times"></i></button>
                        )}
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-6 gap-2">
                      {/* Title — CatalogSelect with quick-add */}
                      <div className="md:col-span-2">
                        <Label className="text-[11px] text-gray-500 mb-1 block">Title *</Label>
                        <CatalogSelect
                          type="payment_title"
                          value={item.title}
                          onChange={(v) => updateItem(idx, { title: v })}
                          placeholder="Select / add title"
                          testId={`item-title-${idx}`}
                          user={user}
                        />
                      </div>
                      {/* Amount + currency */}
                      <div className="md:col-span-2">
                        <Label className="text-[11px] text-gray-500 mb-1 block">Amount *</Label>
                        <div className="flex gap-1">
                          <Select value={item.currency || 'INR'} onValueChange={(v) => updateItem(idx, { currency: v })}>
                            <SelectTrigger className="text-sm w-[95px]" data-testid={`item-currency-${idx}`}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {CURRENCIES.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                            </SelectContent>
                          </Select>
                          <Input
                            type="number"
                            placeholder="0.00"
                            value={item.amount}
                            onChange={(e) => updateItem(idx, { amount: e.target.value })}
                            className="text-sm flex-1"
                            data-testid={`item-amount-${idx}`}
                          />
                        </div>
                        {item.currency && item.currency !== 'INR' && Number(item.amount) > 0 && (
                          <div className="text-[10px] text-emerald-700 mt-1 ml-[100px]" data-testid={`item-inr-eq-${idx}`}>
                            ≈ ₹{inrEq.toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                          </div>
                        )}
                      </div>
                      {/* Due label — CatalogSelect */}
                      <div className="md:col-span-2">
                        <Label className="text-[11px] text-gray-500 mb-1 block">Due</Label>
                        <CatalogSelect
                          type="payment_due_label"
                          value={item.due_label}
                          onChange={(v) => updateItem(idx, { due_label: v })}
                          placeholder="Select / add due label"
                          testId={`item-due-${idx}`}
                          user={user}
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-1 gap-2">
                      <div>
                        <Label className="text-[11px] text-gray-500 mb-1 block">Pay To *</Label>
                        <Select value={item.payee_type || 'indian_company'} onValueChange={(v) => updateItem(idx, { payee_type: v })}>
                          <SelectTrigger className="text-sm" data-testid={`item-payee-type-${idx}`}><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {PAY_TO_OPTIONS.map(pt => <SelectItem key={pt.value} value={pt.value}>{pt.label}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <Textarea
                      placeholder="Notes or description (optional)"
                      rows={1}
                      value={item.description}
                      onChange={(e) => updateItem(idx, { description: e.target.value })}
                      className="text-sm"
                      data-testid={`item-desc-${idx}`}
                    />
                  </div>
                  );
                })}
              </div>
              <Button variant="outline" size="sm" onClick={addItem} className="mt-3" data-testid="add-item-btn">
                <i className="fas fa-plus mr-2"></i>Add Payment Item
              </Button>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditorOpen(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving} className="bg-[#192650] hover:bg-[#0f1a38]" data-testid="save-schedule-btn">
              {saving ? <><i className="fas fa-spinner fa-spin mr-2"></i>Saving...</> : (editing ? 'Save Changes' : 'Create Schedule')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
